import { useCallback, useEffect, useMemo, useState } from 'react';
import { MdArticle, MdLock, MdPhone, MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { RiShieldCheckFill } from 'react-icons/ri';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axiosInstance from '../../axios';
import { isAuthenticated, saveAuth } from '../../utils/auth';
import styles from './Login.module.css';

import LegalDocumentModal from './register/components/LegalDocumentModal';

const TERMS_ENDPOINT = '/legal-documents/terms-and-conditions';
const PRIVACY_ENDPOINT = '/legal-documents/privacy-policy';

const Login = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [form, setForm] = useState({ login: '', password: '' });
    const [agreeTerms, setAgreeTerms] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({ login: '', password: '' });

    const [documentsLoading, setDocumentsLoading] = useState(true);
    const [documentsError, setDocumentsError] = useState('');
    const [legalDocuments, setLegalDocuments] = useState({
        terms_and_conditions: null,
        privacy_policy: null,
    });

    const [selectedDocument, setSelectedDocument] = useState(null);
    const [isLegalModalOpen, setIsLegalModalOpen] = useState(false);

    const redirectTo = useMemo(() => {
        const from = location.state?.from;
        if (from?.pathname) {
            return `${from.pathname}${from.search || ''}${from.hash || ''}`;
        }
        return '/dashboard';
    }, [location.state]);

    const loadLegalDocuments = useCallback(async () => {
        try {
            setDocumentsLoading(true);
            setDocumentsError('');

            const [termsResponse, privacyResponse] = await Promise.all([
                axiosInstance.get(TERMS_ENDPOINT, { skipAuthRedirect: true }),
                axiosInstance.get(PRIVACY_ENDPOINT, { skipAuthRedirect: true }),
            ]);

            const termsDocument = termsResponse?.data?.data ?? termsResponse?.data ?? null;
            const privacyDocument = privacyResponse?.data?.data ?? privacyResponse?.data ?? null;

            setLegalDocuments({
                terms_and_conditions: termsDocument,
                privacy_policy: privacyDocument,
            });
        } catch (err) {
            console.error(err);
            setDocumentsError(err?.apiMessage || 'Dokumen legal gagal dimuat.');
        } finally {
            setDocumentsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadLegalDocuments();
    }, [loadLegalDocuments]);

    const openDocument = useCallback(
        (type) => {
            const document = legalDocuments?.[type];

            if (!document?.content) {
                setError('Dokumen belum tersedia.');
                return;
            }

            setSelectedDocument(document);
            setIsLegalModalOpen(true);
        },
        [legalDocuments]
    );

    const closeDocument = useCallback(() => {
        setIsLegalModalOpen(false);
        setSelectedDocument(null);
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        if (params.get('session') === 'expired') {
            setError('Sesi Anda sudah habis. Silakan login kembali.');
        } else if (location.state?.message) {
            setError(location.state.message);
        }

        if (isAuthenticated()) {
            navigate(redirectTo, { replace: true });
        }
    }, [navigate, redirectTo, location.search, location.state]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        setForm((prev) => ({ ...prev, [name]: value }));
        setError('');
        setFieldErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const setAuthFieldError = (message) => {
        const text = message || 'Nomor WhatsApp/email atau kata sandi salah.';
        setFieldErrors({
            login: text,
            password: text,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const loginValue = form.login.trim();
        const passwordValue = form.password;

        if (!loginValue || !passwordValue) {
            setError('Nomor WhatsApp/email dan kata sandi wajib diisi.');
            return;
        }

        setLoading(true);
        setError('');
        setFieldErrors({ login: '', password: '' });

        try {
            const response = await axiosInstance.post(
                '/auth/login',
                {
                    login: loginValue,
                    password: passwordValue,
                },
                {
                    validateStatus: (status) => status < 500,
                    skipAuthRedirect: true,
                }
            );

            const responseData = response?.data || {};

            if (!responseData?.success) {
                const backendErrors = responseData?.errors || {};
                const apiMessage = responseData?.message || '';

                if (response.status === 422) {
                    setFieldErrors({
                        login: backendErrors.login?.[0] || '',
                        password: backendErrors.password?.[0] || '',
                    });
                    setError(apiMessage || 'Data login belum valid.');
                    return;
                }

                if (response.status === 401) {
                    setAuthFieldError(apiMessage);
                    setError(apiMessage || 'Nomor WhatsApp/email atau kata sandi salah.');
                    return;
                }

                if (response.status === 403) {
                    setError(apiMessage || 'Akun belum aktif atau belum diverifikasi.');
                    return;
                }

                setError(apiMessage || 'Login gagal. Periksa kembali data Anda.');
                return;
            }

            const token = responseData?.data?.token;
            const user = responseData?.data?.user;
            const expiresIn = responseData?.data?.expires_in;

            if (!token || !user) {
                setError('Respons login tidak valid.');
                return;
            }

            saveAuth({
                token,
                user,
                expiresInSeconds: Number(expiresIn),
            });

            navigate(redirectTo, { replace: true });
        } catch (err) {
            setError(err?.apiMessage || 'Login gagal. Periksa kembali data Anda.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <div className={styles.page}>
            <section className={styles.heroSide} style={{ background: 'var(--color-primary)' }}>
                <div className={styles.heroCard}>
                    <div className={styles.brandBadge}>
                        <RiShieldCheckFill className={styles.brandBadgeIcon} />
                        <span>Property Access</span>
                    </div>

                    <h1 className={styles.heroTitle}>Masuk ke dashboard properti.</h1>
                    <p className={styles.heroText}>Kelola listing dan monitoring analytics.</p>

                    <ul className={styles.featureList}>
                        <li>Pilihan property berkualitas yang tersertifikasi melalui inspeksi.</li>
                        <li>Pengelolaan properti yang lebih baik.</li>
                        <li>Temukan rumah impian Anda bersama kami.</li>
                    </ul>
                </div>
            </section>

            <section className={styles.formSide}>
                <div className={styles.formCard}>
                    <div className={styles.logoWrap}>
                        <img src="/logo-dark.png" alt="Logo" className={styles.logo} />
                    </div>

                    <div className={styles.formHeader}>
                        <span className={styles.kicker}>Welcome Back</span>
                        <h2 className={styles.formTitle}>Masuk ke Akun</h2>
                        <p className={styles.formSub}>
                            Belum punya akun?{' '}
                            <Link to="/register" className={styles.link}>
                                Daftar gratis
                            </Link>
                        </p>
                    </div>

                    {documentsError && <div className={styles.warningBox}>{documentsError}</div>}

                    {error && (
                        <div className={styles.errorBox} role="alert" aria-live="polite">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} noValidate className={styles.form}>
                        <div className={styles.field}>
                            <label className={styles.label} htmlFor="login">
                                Nomor WhatsApp / Email
                            </label>

                            <div className={styles.inputWrap}>
                                <MdPhone className={styles.iconLeft} size={18} />
                                <input
                                    id="login"
                                    className={`${styles.input} ${fieldErrors.login ? styles.inputError : ''}`}
                                    type="text"
                                    name="login"
                                    placeholder="08xx-xxxx-xxxx atau email"
                                    value={form.login}
                                    onChange={handleChange}
                                    autoComplete="username"
                                    autoCapitalize="none"
                                    autoCorrect="off"
                                    aria-invalid={!!fieldErrors.login}
                                    aria-describedby={fieldErrors.login ? 'login-error' : undefined}
                                />
                            </div>

                            {fieldErrors.login && (
                                <span id="login-error" className={styles.fieldError} role="alert">
                                    {fieldErrors.login}
                                </span>
                            )}
                        </div>

                        <div className={styles.field}>
                            <div className={styles.fieldHeaderRow}>
                                <label className={styles.label} htmlFor="password">
                                    Kata Sandi
                                </label>

                                <Link to="/forgot-password" className={styles.forgotLink}>
                                    Lupa password?
                                </Link>
                            </div>

                            <div className={styles.inputWrap}>
                                <MdLock className={styles.iconLeft} size={18} />
                                <input
                                    id="password"
                                    className={`${styles.input} ${styles.inputPadRight} ${
                                        fieldErrors.password ? styles.inputError : ''
                                    }`}
                                    type={showPassword ? 'text' : 'password'}
                                    name="password"
                                    placeholder="Masukkan kata sandi"
                                    value={form.password}
                                    onChange={handleChange}
                                    autoComplete="current-password"
                                    aria-invalid={!!fieldErrors.password}
                                    aria-describedby={fieldErrors.password ? 'password-error' : undefined}
                                />

                                <button
                                    type="button"
                                    className={styles.toggleBtn}
                                    onClick={() => setShowPassword((v) => !v)}
                                    aria-label={showPassword ? 'Sembunyikan kata sandi' : 'Tampilkan kata sandi'}
                                >
                                    {showPassword ? <MdVisibilityOff size={19} /> : <MdVisibility size={19} />}
                                </button>
                            </div>

                            {fieldErrors.password && (
                                <span id="password-error" className={styles.fieldError} role="alert">
                                    {fieldErrors.password}
                                </span>
                            )}
                        </div>

                        <div className={styles.agreementField}>
                            <div className={styles.agreementCard}>
                                <div className={styles.agreementTitleWrap}>
                                    <MdArticle className={styles.agreementIcon} />
                                    <div>
                                        <p className={styles.agreementTitle}>Dokumen Legal</p>
                                        <p className={styles.agreementSubtitle}>
                                            Dengan melanjutkan, Anda menyetujui dokumen legal platform.
                                        </p>
                                    </div>
                                </div>

                                <div className={styles.agreementLinks}>
                                    <button
                                        type="button"
                                        className={styles.inlineLinkBtn}
                                        onClick={() => openDocument('terms_and_conditions')}
                                        disabled={documentsLoading}
                                    >
                                        Syarat dan Ketentuan
                                    </button>

                                    <span className={styles.linkSeparator}>dan</span>

                                    <button
                                        type="button"
                                        className={styles.inlineLinkBtn}
                                        onClick={() => openDocument('privacy_policy')}
                                        disabled={documentsLoading}
                                    >
                                        Kebijakan Privasi
                                    </button>
                                </div>

                                <label className={styles.checkboxCard}>
                                    <input
                                        type="checkbox"
                                        checked={agreeTerms}
                                        onChange={(e) => setAgreeTerms(e.target.checked)}
                                        disabled={loading}
                                    />
                                    <span>Saya menyetujui dokumen legal di atas</span>
                                </label>
                            </div>
                        </div>

                        <button type="submit" className={styles.submitBtn} disabled={loading || !agreeTerms}>
                            {loading ? <span className={styles.spinner} aria-hidden="true" /> : <>Masuk Sekarang</>}
                        </button>
                    </form>
                </div>
            </section>
        </div>

            <LegalDocumentModal
                open={isLegalModalOpen}
                document={selectedDocument}
                onClose={closeDocument}
                onConfirmRead={closeDocument}
            />
        </>
    );
};

export default Login;
